import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { saveWithRetry, errorMessage } from "@/lib/saveWithRetry";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2, Percent, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Bulk Staff Discount editor.
 *
 * Lets the owner set (or clear) `staff_discount_percent` on many products
 * at once. Filter by category / department / supplier, tick the rows, punch
 * in a % and confirm. Backend hits `POST /api/products/bulk-set-staff-discount`.
 */
export default function BulkStaffDiscountModal({ open, onClose, suppliers = [], categories = [], departments = [], onComplete }) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ANY");
  const [departmentFilter, setDepartmentFilter] = useState("ANY");
  const [supplierFilter, setSupplierFilter] = useState("ANY");
  const [currentPctFilter, setCurrentPctFilter] = useState("ANY"); // ANY | HAS | NONE
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [targetPct, setTargetPct] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // `/products` caps limit at 5000. For larger catalogs we page
      // through in chunks and concat.
      const CHUNK = 5000;
      let all = [];
      let skip = 0;
      // Safety cap — a Bosveld-sized catalog is well under 50k, and
      // this loop bails as soon as a partial chunk is returned.
      for (let i = 0; i < 20; i += 1) {
        const r = await axios.get(`${API}/api/products?limit=${CHUNK}&skip=${skip}`);
        const batch = Array.isArray(r.data) ? r.data : (r.data.products || []);
        all = all.concat(batch);
        if (batch.length < CHUNK) break;
        skip += CHUNK;
      }
      setProducts(all);
    } catch {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setSearch("");
      setCategoryFilter("ANY");
      setDepartmentFilter("ANY");
      setSupplierFilter("ANY");
      setCurrentPctFilter("ANY");
      setTargetPct("");
      setConfirming(false);
      fetchProducts();
    }
  }, [open]);

  const supplierLookup = useMemo(() => {
    const map = {};
    for (const s of suppliers) map[s.id] = s.name;
    return map;
  }, [suppliers]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((p) => {
      if (categoryFilter !== "ANY" && (p.category || "") !== categoryFilter) return false;
      if (departmentFilter !== "ANY" && (p.department || "") !== departmentFilter) return false;
      if (supplierFilter !== "ANY") {
        if (supplierFilter === "UNASSIGNED") {
          if (p.supplier_id || p.supplier) return false;
        } else if (p.supplier_id !== supplierFilter) {
          return false;
        }
      }
      const pct = Number(p.staff_discount_percent) || 0;
      if (currentPctFilter === "HAS" && pct <= 0) return false;
      if (currentPctFilter === "NONE" && pct > 0) return false;
      if (!term) return true;
      const blob = `${p.plu_code || ""} ${p.name || ""} ${p.barcode || ""}`.toLowerCase();
      return blob.includes(term);
    });
  }, [products, search, categoryFilter, departmentFilter, supplierFilter, currentPctFilter]);

  const toggleId = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const toggleAllVisible = (checked) => {
    const next = new Set(selectedIds);
    for (const p of filtered) {
      if (checked) next.add(p.id);
      else next.delete(p.id);
    }
    setSelectedIds(next);
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));

  const parsedPct = (() => {
    const raw = String(targetPct).trim();
    if (raw === "") return null;
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    return n;
  })();

  const pctValid = parsedPct !== null && parsedPct >= 0 && parsedPct <= 100;

  const summary = useMemo(() => {
    let willChange = 0;
    let alreadyThere = 0;
    for (const p of products) {
      if (!selectedIds.has(p.id)) continue;
      const current = Number(p.staff_discount_percent) || 0;
      if (parsedPct !== null && Math.abs(current - parsedPct) < 0.001) alreadyThere += 1;
      else willChange += 1;
    }
    return { totalSelected: selectedIds.size, willChange, alreadyThere };
  }, [products, selectedIds, parsedPct]);

  const handleStartConfirm = () => {
    if (!pctValid) {
      toast.error("Enter a % between 0 and 100 (use 0 to clear)");
      return;
    }
    if (selectedIds.size === 0) {
      toast.error("Select at least one product");
      return;
    }
    setConfirming(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const r = await saveWithRetry(() => axios.post(`${API}/api/products/bulk-set-staff-discount`, {
        staff_discount_percent: parsedPct,
        product_ids: Array.from(selectedIds),
      }));
      toast.success(r.data?.message || `Updated ${r.data?.products_updated} products`);
      setConfirming(false);
      setSelectedIds(new Set());
      if (onComplete) onComplete();
      onClose && onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk staff-discount update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="bulk-staff-discount-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-indigo-600" />
            Bulk Staff Discount
          </DialogTitle>
          <DialogDescription>
            Filter, tick, punch in a % and apply. Discount is applied silently at the till whenever the
            selected customer is flagged as <strong>Staff</strong> and the product has a non-zero
            staff&nbsp;% here. Enter <code className="text-xs bg-gray-100 px-1 rounded">0</code> to clear.
          </DialogDescription>
        </DialogHeader>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-200">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search PLU, name, barcode…"
              className="pl-9"
              data-testid="bulk-staff-search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]" data-testid="bulk-staff-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="ANY">Any category</SelectItem>
              {categories.map((c) => {
                const name = typeof c === "string" ? c : c?.name;
                if (!name) return null;
                return <SelectItem key={name} value={name}>{name}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-[180px]" data-testid="bulk-staff-department-filter">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="ANY">Any department</SelectItem>
              {departments.map((d) => {
                const name = typeof d === "string" ? d : d?.name;
                if (!name) return null;
                return <SelectItem key={name} value={name}>{name}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Select value={supplierFilter} onValueChange={setSupplierFilter}>
            <SelectTrigger className="w-[180px]" data-testid="bulk-staff-supplier-filter">
              <SelectValue placeholder="Supplier" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="ANY">Any supplier</SelectItem>
              <SelectItem value="UNASSIGNED">Unassigned only</SelectItem>
              {suppliers.map((s) => {
                if (!s?.id || !s?.name) return null;
                return <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Select value={currentPctFilter} onValueChange={setCurrentPctFilter}>
            <SelectTrigger className="w-[180px]" data-testid="bulk-staff-current-filter">
              <SelectValue placeholder="Current staff %" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ANY">Any current %</SelectItem>
              <SelectItem value="NONE">No staff % yet</SelectItem>
              <SelectItem value="HAS">Already has staff %</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Product list */}
        <div className="flex-1 min-h-[260px] overflow-y-auto border border-gray-200 rounded-lg mt-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading products…</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-sm">No products match the current filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 text-xs uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={(c) => toggleAllVisible(!!c)}
                      data-testid="bulk-staff-select-all"
                    />
                  </th>
                  <th className="text-left px-3 py-2">PLU</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-right px-3 py-2">Current Staff %</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((p) => {
                  const isSelected = selectedIds.has(p.id);
                  const pct = Number(p.staff_discount_percent) || 0;
                  return (
                    <tr
                      key={p.id}
                      className={`border-t border-gray-100 hover:bg-gray-50 ${isSelected ? "bg-indigo-50" : ""}`}
                      data-testid={`bulk-staff-row-${p.id}`}
                    >
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleId(p.id)}
                          data-testid={`bulk-staff-select-${p.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{p.plu_code || "—"}</td>
                      <td className="px-3 py-2">{p.name}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{p.category || "—"}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        {pct > 0 ? (
                          <span className="text-indigo-700 font-medium">{pct}%</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length > 500 && (
                  <tr>
                    <td colSpan="5" className="px-3 py-2 text-xs italic text-gray-500 bg-amber-50">
                      Showing first 500 of {filtered.length}. Refine the filter to see more.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Selection summary + target */}
        <div className="border border-indigo-200 bg-indigo-50 rounded-lg p-3 mt-3 space-y-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">
              <strong>{selectedIds.size}</strong> product{selectedIds.size === 1 ? "" : "s"} selected
              {filtered.length > 0 && (
                <span className="text-gray-500 ml-1">(of {filtered.length} visible)</span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={selectedIds.size === 0}
              data-testid="bulk-staff-clear-selection"
            >
              Clear selection
            </Button>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-sm font-medium min-w-[110px]">Set staff discount %:</Label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={targetPct}
                onChange={(e) => setTargetPct(e.target.value)}
                className="w-28 bg-white"
                placeholder="0"
                data-testid="bulk-staff-target-input"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            <span className="text-xs text-gray-500">Use <code className="bg-white px-1 rounded">0</code> to clear the staff discount.</span>
          </div>
        </div>

        {/* Confirmation block */}
        {confirming && (
          <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-4 mt-3" data-testid="bulk-staff-confirm-summary">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm space-y-1">
                <div className="font-bold text-amber-900">Confirm before applying</div>
                <div>
                  About to set <strong>{parsedPct}%</strong> staff discount on <strong>{summary.willChange}</strong> product{summary.willChange === 1 ? "" : "s"}.
                </div>
                {summary.alreadyThere > 0 && (
                  <div className="text-amber-700">
                    ({summary.alreadyThere} of the {summary.totalSelected} selected already have this exact % — they&apos;ll simply be re-stamped.)
                  </div>
                )}
                {parsedPct === 0 && (
                  <div className="text-amber-800 font-medium">
                    ⚠ Setting to 0% clears the staff discount — those products will no longer give staff any extra off.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} data-testid="bulk-staff-cancel-btn">
            Cancel
          </Button>
          {!confirming ? (
            <Button
              onClick={handleStartConfirm}
              disabled={selectedIds.size === 0 || !pctValid}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="bulk-staff-review-btn"
            >
              Review changes
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="bulk-staff-confirm-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Apply {parsedPct}% to {summary.willChange} product{summary.willChange === 1 ? "" : "s"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
